/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { transformAccount } from '../data/transforms';

const ACCOUNT_CONTEXT = 'accountContext';

enum EventsList {
  EDIT_ACCOUNT_EVENT = 'edit-account',
}

const events = {
  // Reference - https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/editAccountAEP.ts
  EDIT_ACCOUNT_EVENT: EventsList.EDIT_ACCOUNT_EVENT,
};

export function getAdobeDataLayer() {
  // @ts-ignore
  window.adobeDataLayer = window.adobeDataLayer || [];
  // @ts-ignore
  return window.adobeDataLayer;
}

/**
 * Sets a context in the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/Base.ts#L6
 */
function setContext(name: string, data: any) {
  const adobeDataLayer = getAdobeDataLayer();

  // Clear existing context
  adobeDataLayer.push({
    [name]: null,
  });

  // Set new context
  adobeDataLayer.push({
    [name]: data,
  });
}

/**
 * Pushes an event to the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/1973d0ce28471ef190fa06dad6359ffa0ab51db6/packages/storefront-events-sdk/src/Base.ts#L34
 */
function pushEvent(event: string, additionalContext?: any) {
  const adobeDataLayer = getAdobeDataLayer();

  adobeDataLayer.push((acdl: any) => {
    const state = acdl.getState ? acdl.getState() : {};

    acdl.push({
      event,
      eventInfo: {
        ...state,
        ...additionalContext,
      },
    });
  });
}

/**
 * References:
 * https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/handlers/account/editAccountAEP.ts
 * https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-collector/src/utils/aep/account.ts
 */
function editAccountEvent(eventData: any) {
  const accountData = transformAccount(eventData);

  setContext(ACCOUNT_CONTEXT, accountData);

  pushEvent(events.EDIT_ACCOUNT_EVENT);
}

const publishEvents = (eventType: string, eventParams: any) => {
  const storeConfigRaw = sessionStorage.getItem('accountStoreConfig');
  const storeConfig = storeConfigRaw ? JSON.parse(storeConfigRaw) : {};
  const eventData = { ...storeConfig, ...eventParams };

  switch (eventType) {
    case EventsList.EDIT_ACCOUNT_EVENT:
      editAccountEvent(eventData);
      break;

    default:
      return null;
  }
};

export { EventsList, publishEvents, pushEvent };
